import React, { useState, useEffect, useCallback, useRef } from 'react';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import BulkNotificationEditor from '../Components/BulkNotificationEditor';
import Loader from '../Components/Loader';
import MadeWithLove from '../Components/MadeWithLove';
import { 
  DataGrid, 
  GridToolbarContainer, 
  GridToolbarColumnsButton, 
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport 
} from '@mui/x-data-grid';
import AppContainer from '../Components/AppContainer';
import InternalApi from '../Utils/InternalApi';
import CSVUploader from '../Components/CSVUploader';
import { InputAdornment, TextField } from '@material-ui/core';
import withStyle from '../style';
import Networker from '../Utils/Networker';
import { downloadObjectData } from '../Utils/DataUtils';
import Button from '@material-ui/core/Button';
import ConsumersTable from '../Components/EmailConsumersStatTable';
import { IconButton } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import LinearProgress from '@material-ui/core/LinearProgress';

const getAllAdminProducts = () => {
  return Networker.get({
    root: 'products',
    inner: 'admin',
    cache: true
  });
};

const getAllAdminOrganiztions = () => {
  return Networker.get({
    root: 'organizations',
    inner: 'admin',
    cache: true
  });
};

const getAllSUProducts = () => {
  return Networker.get({
    root: 'products',
    inner: 'all',
    cache: true
  });
};

const getUserSession = () => {
  return Networker.getUserSession();
};

// This is a silly thing to do.
const ProductIdSingletonFactory = (function () {
  function ProductIdClass() {
    this.setValue = (newValue) => { this.value = newValue; }
    this.getValue = () => { return this.value; }

    this.value = undefined;
  }

  let instance;

  return {
    getInstance: function () {
      if (!instance) {
        instance = new ProductIdClass();
        delete instance.constructor;
      }

      return instance;
    }
  }
})();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[1-9]\d{9,14}$/;

function isValidEmail(email) {
  return !!email && EMAIL_REGEX.test(String(email).trim());
}

function normalizePhone(phone) {
  return String(phone || '').replace(/[^\d+]/g, '');
}

function normalizePermission(value) {
  return String(value || '').trim().toLowerCase();
}

function isValidPhone(phone) {
  const normalized = normalizePhone(phone);
  return PHONE_REGEX.test(normalized);
}

function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === '';
}

function daysSince(dateValue) {
  if (!dateValue) return null;
  const dt = new Date(dateValue);
  if (Number.isNaN(dt.getTime())) return null;
  return (Date.now() - dt.getTime()) / (24 * 60 * 60 * 1000);
}

function enrichConnectionRow(row, thresholds = {}) {
  const disconnectedDays = thresholds.disconnectedDays ?? 3;
  const staleDeviceStatusDays = thresholds.staleDeviceStatusDays ?? 3;
  const lowBatteryThreshold = thresholds.lowBatteryThreshold ?? 2.78;

  const lastInteractionDays = daysSince(row.lastInteractionDate);
  const lastConnectionDays =
    row.lastConnectionMs !== null && row.lastConnectionMs !== undefined && row.lastConnectionMs !== ''
      ? Number(row.lastConnectionMs)
      : null;

  const deviceStatusAgeDays = daysSince(row.deviceStatusUpdatedAt);

  const emailValid = isValidEmail(row.email);
  const phoneValid = isValidPhone(row.phone);

  const lowBattery =
    row.battery !== null && row.battery !== undefined && row.battery !== ''
      ? Number(row.battery) <= lowBatteryThreshold
      : false;

  const missingDeviceVendorIdentifier = isBlank(row.deviceVendorIdentifier);

  const userNotConnected =
    (lastInteractionDays !== null && lastInteractionDays > disconnectedDays) ||
    (lastConnectionDays !== null && lastConnectionDays > disconnectedDays);

  const staleDeviceStatus =
    deviceStatusAgeDays !== null && deviceStatusAgeDays > staleDeviceStatusDays;

  const scanNotRunning =
    Array.isArray(row.deviceScanRunning)
      ? row.deviceScanRunning[0] === false
      : row.deviceScanRunning === false;
  const bleOff = String(row.deviceBluetoothState || '').trim().toLowerCase() === 'off';
  const deviceStatusUnknown = isBlank(row.deviceAppState);

  const bluetoothPermission = normalizePermission(row.devicePermissionBluetooth);
  const locationPermission = normalizePermission(row.devicePermissionLocation);
  const pushPermission = normalizePermission(row.devicePermissionPush);

  const bluetoothPermissionDenied =
    bluetoothPermission !== '' &&
    bluetoothPermission !== 'allowed';

  const locationPermissionDenied =
    locationPermission !== '' &&
    locationPermission !== 'allowed' &&
    locationPermission !== 'precise' &&
    locationPermission !== 'approximate';

  const locationPermissionNotPrecise =
    locationPermission === 'approximate';

  const pushPermissionDenied =
    pushPermission !== '' &&
    pushPermission !== 'allowed';

  const devicePermissionsUnknown =
    isBlank(row.devicePermissionBluetooth) &&
    isBlank(row.devicePermissionLocation) &&
    isBlank(row.devicePermissionPush);

  const hasMultipleMacs =
    String(row.labels || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .length > 1;

  return {
    ...row,
    derived: {
      lowBattery,
      pushEnabled: row.notificationPushEnabled === true,
      smsEnabled: row.notificationSmsEnabled === true,
      emailEnabled: row.notificationEmailEnabled === true,
      validPhone: phoneValid,
      validEmail: emailValid,
      missingDeviceVendorIdentifier,
      userNotConnected,
      staleDeviceStatus,
      scanNotRunning,
      bleOff,
      deviceStatusUnknown,
      bluetoothPermission,
      locationPermission,
      pushPermission,
      bluetoothPermissionDenied,
      locationPermissionDenied,
      locationPermissionNotPrecise,
      pushPermissionDenied,
      devicePermissionsUnknown,
      hasMultipleMacs,
      lastInteractionDays,
      lastConnectionDays,
      deviceStatusAgeDays,
    }
  };
}

const CRITERIA_DEFINITIONS = {
  lowBattery: {
    label: 'Battery Low',
    matches: (row) => row.derived.lowBattery === true,
  },
  pushDisabled: {
    label: 'Push Disabled',
    matches: (row) => row.derived.pushEnabled === false,
  },
  smsDisabled: {
    label: 'SMS Disabled',
    matches: (row) => row.derived.smsEnabled === false,
  },
  emailDisabled: {
    label: 'Email Disabled',
    matches: (row) => row.derived.emailEnabled === false,
  },
  invalidPhone: {
    label: 'Invalid Phone',
    matches: (row) => row.derived.validPhone === false,
  },
  invalidEmail: {
    label: 'Invalid Email',
    matches: (row) => row.derived.validEmail === false,
  },
  missingDeviceVendorIdentifier: {
    label: 'Missing Device Vendor Identifier',
    matches: (row) => row.derived.missingDeviceVendorIdentifier === true,
  },
  userNotConnected: {
    label: 'Not Connected',
    matches: (row) => row.derived.userNotConnected === true,
  },
  staleDeviceStatus: {
    label: 'Stale Device Status',
    matches: (row) => row.derived.staleDeviceStatus === true,
  },
  scanNotRunning: {
    label: 'BLE Scan Off',
    matches: (row) => row.derived.scanNotRunning === true,
  },
  bleOff: {
    label: 'BLE Off',
    matches: (row) => row.derived.bleOff === true,
  },
  deviceStatusUnknown: {
    label: 'Device Status Unknown',
    matches: (row) => row.derived.deviceStatusUnknown === true,
  },
  devicePermissionsUnknown: {
    label: 'Device Permissions Unknown',
    matches: (row) => row.derived.devicePermissionsUnknown === true,
  },
  bluetoothPermissionDenied: {
    label: 'Bluetooth Permission Denied',
    matches: (row) => row.derived.bluetoothPermissionDenied === true,
  },
  locationPermissionDenied: {
    label: 'Location Permission Denied',
    matches: (row) => row.derived.locationPermissionDenied === true,
  },
  locationPermissionNotPrecise: {
    label: 'Location Not Precise',
    matches: (row) => row.derived.locationPermissionNotPrecise === true,
  },
  pushPermissionDenied: {
    label: 'Push Permission Denied',
    matches: (row) => row.derived.pushPermissionDenied === true,
  },
  invalidFirebaseToken: {
    label: 'Missing Firebase Token',
    matches: (row) => isBlank(row.firebaseToken),
  },
  multipleMacs: {
    label: 'Multiple Macs',
    matches: (row) => row.derived.hasMultipleMacs === true,
  },
};

function ConnectionStatsToolbar(props) {
  const {
    isSuperUser,
    stats,
    activeCriteria,
    setActiveCriteria,
    thresholds,
    setThresholds,
    selectedCount,
    onOpenBulkNotify,
  } = props;

  const toggleCriteria = (key) => {
    setActiveCriteria((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  };

  return (
    <GridToolbarContainer style={toolbarStyles.root}>
      <div style={toolbarStyles.group}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </div>

      {isSuperUser && (
        <>
          <div style={toolbarStyles.thresholdsGroup}>
            <div style={toolbarStyles.fieldBlock}>
              <Typography variant="caption" style={toolbarStyles.fieldLabel}>
                Battery &lt;=                
              </Typography>
              <TextField
                type="number"
                size="small"
                variant="outlined"
                value={thresholds.lowBatteryThreshold}
                onChange={(e) =>
                  setThresholds((prev) => ({
                    ...prev,
                    lowBatteryThreshold: Number(e.target.value),
                  }))
                }
                style={toolbarStyles.textField}
                inputProps={{
                  step: 0.01,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ color: '#888', fontSize: 12 }}>Volts</span>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div style={toolbarStyles.fieldBlock}>
              <Typography variant="caption" style={toolbarStyles.fieldLabel}>
                Last Connection &gt;
              </Typography>
              <TextField
                type="number"
                size="small"
                variant="outlined"
                value={thresholds.disconnectedDays}
                onChange={(e) =>
                  setThresholds((prev) => ({
                    ...prev,
                    disconnectedDays: Number(e.target.value),
                  }))
                }
                style={toolbarStyles.textField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ color: '#888', fontSize: 12 }}>days</span>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div style={toolbarStyles.fieldBlock}>
              <Typography variant="caption" style={toolbarStyles.fieldLabel}>
                Device Status Last Updated &gt;                
              </Typography>
              <TextField
                type="number"
                size="small"
                variant="outlined"
                value={thresholds.staleDeviceStatusDays}
                onChange={(e) =>
                  setThresholds((prev) => ({
                    ...prev,
                    staleDeviceStatusDays: Number(e.target.value),
                  }))
                }
                style={toolbarStyles.textField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span style={{ color: '#888', fontSize: 12 }}>days</span>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div style={toolbarStyles.thresholdsSpacer} />
            
            <Button
              variant="contained"
              color="secondary"
              style={toolbarStyles.sendButton}
              onClick={onOpenBulkNotify}
              disabled={selectedCount === 0}
            >
              Send Message to Selected Users ({selectedCount})
            </Button>
          </div>

          <div style={toolbarStyles.criteriaGroup}>
            {Object.entries(CRITERIA_DEFINITIONS).map(([key, def]) => (
              <Button
                key={key}
                size="small"
                variant={activeCriteria.includes(key) ? 'contained' : 'outlined'}
                color="primary"
                style={toolbarStyles.button}
                onClick={() => toggleCriteria(key)}
              >
                {def.label} ({stats[key] || 0})
              </Button>
            ))}

            {!!activeCriteria.length && (
              <Button
                size="small"
                onClick={() => setActiveCriteria([])}
              >
                Clear
              </Button>
            )}
          </div>
        </>
      )}
    </GridToolbarContainer>
  );
}

export default function ConnectionStatistics({ match, history }) {
  const [loading, setLoading] = useState(true);

  const [hasAccess, setHasAccess] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false); // to hide some features for non super users
  const [sidebarVisible, setSidebarVisible] = useState(true);

  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [displayProducts, setDisplayProducts] = useState(products);

  const [openConsumersStack, setOpenConsumersStack] = useState(false);
  const [consumersStackData, setConsumersStackData] = useState([]);

  // `tableData` is given as a download. `tableRows` has an extra `id` key added to allow the DataGrid to layout properly.
  const [tableHeaders, setTableHeaders] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [rowCount, setRowCount] = useState(100);

  const [consumerData, setConsumerData] = useState(false)
  const [sortObject, setSortObject] = useState({ field: 'email', order: 1 });

  const [ShowMatchedEmails, setShowMatchedEmails] = React.useState(false);

  const [thresholds, setThresholds] = useState({
    lowBatteryThreshold: 2.78,
    disconnectedDays: 3,
    staleDeviceStatusDays: 3,
  });

  const [activeCriteria, setActiveCriteria] = useState([]);

  /* Store a reference to the active WebSocket */
  const socket = useRef(null);

  const currentTab = match.params.productId;
  const ConsumersListDetailsFromApi = useRef([]);

  const currentProduct = React.useMemo(() => {
    return products.find((product) => product._id === currentTab) || {};
  }, [products, currentTab]);

  const progressListenerRef = useRef(null);

  const [loadBar, setLoadBar] = useState({ active: false, value: 0, label: '' });
  const loadBarTimerRef = useRef(null);
  const activeRequestIdRef = useRef(null);

  const [selectionModel, setSelectionModel] = useState([]);
  const [bulkNotifyOpen, setBulkNotifyOpen] = useState(false);

  const stopLoadBarTimer = () => {
    if (loadBarTimerRef.current) {
      clearInterval(loadBarTimerRef.current);
      loadBarTimerRef.current = null;
    }
  };

  const startLoadBar = (label) => {
    setLoadBar({ active: true, value: 0, label: label || 'Loading...' });
  };

  const bumpLoadBarTowardCap = useCallback(() => {
    setLoadBar((prev) => {
      if (!prev.active) return prev;

      const cap = 99;

      // Distance to cap
      const remaining = cap - prev.value;

      // Faster movement: take a larger % of remaining
      let delta = remaining * 0.25;

      // Ensure minimum jump so it never feels stuck
      delta = Math.max(delta, 2);

      // Prevent overshooting
      const next = Math.min(prev.value + delta, cap);

      return {
        ...prev,
        value: next
      };
    });
  }, []);

  const finishLoadBar = async () => {
    // Quickly animate to 100
    await new Promise((resolve) => {
      const interval = setInterval(() => {
        setLoadBar((prev) => {
          if (!prev.active) {
            clearInterval(interval);
            resolve();
            return prev;
          }

          const next = Math.min(100, prev.value + 4.5);
          if (next >= 100) {
            clearInterval(interval);
            resolve();
            return { ...prev, value: 100 };
          }
          return { ...prev, value: next };
        });
      }, 16);
    });

    await new Promise((r) => setTimeout(r, 200));
    setLoadBar({ active: false, value: 0, label: '' });
  };

  const cancelLoadBar = () => {
    setLoadBar({ active: false, value: 0, label: '' });
  };

  useEffect(() => {
    return () => stopLoadBarTimer();
  }, []);

  ProductIdSingletonFactory.getInstance().setValue(currentTab);

  // Filter list of products by given query string.
  const filteredProducts = (products, query) => products.filter((product) => product.name.toLowerCase().includes(query.toLowerCase()));

  const handleChangeQuery = (event) => {
    setDisplayProducts(filteredProducts(products, event.target.value));
    setQuery(event.target.value);
  };

  // --- RECONNECT SUPPORT ---
  const showSocketError = useCallback(() => {
    window.alert('WebSocket connection failed.');
  }, []);

  const reconnectSocket = useCallback(async () => {
    const s = socket.current;
    if (s && s.readyState === WebSocket.OPEN) return s;

    try {
      const newSocket = await InternalApi.connect(); // no onMessage handler needed here
      socket.current = newSocket;

      // Optional: clean ref on close/error so future calls reconnect
      newSocket.addEventListener('close', () => {
        console.warn('Socket closed.');
        socket.current = null;
      });
      newSocket.addEventListener('error', () => {
        console.warn('Socket error.');
        try { newSocket.close(); } catch (_) {}
        socket.current = null;
      });

      return newSocket;
    } catch (error) {
      console.error('Failed to reconnect to socket:', error);
      showSocketError();
      throw new Error('WebSocket reconnection failed');
    }
  }, [showSocketError]);

  const refreshProducts = useCallback(async () => {
    setLoading(true);

    try {
      const orgs = await getAllAdminOrganiztions();
      const res = await getUserSession();

      const access = orgs.body?.some((o) => !!o.connectionStatsAccess);
      setIsSuperUser(!!res.body?.isSuperUser);

      // Set this only once since it's tied to the lifecycle of our websocket.
      if (!hasAccess && (access || res.body.isSuperUser)) {
        setHasAccess(true);
      } else {
        Networker.interactionBeacon({ message: 'non-upgraded consumer connect view' });
      }

      const prods = await (res.body.isSuperUser ? getAllSUProducts : getAllAdminProducts)();
      const filtered = Array.from(new Set(prods.body)).sort((a, b) => a.name > b.name ? 0 : -1);

      setProducts(filtered);
      setDisplayProducts(filteredProducts(filtered, query));
    } catch (e) {
      reportNetworkError(e, 'connection statistics products fetch error');
    } finally {
      setLoading(false);
    }
  // This is stupid.
  // eslint-disable-next-line
  }, []);

  useEffect(refreshProducts, [ refreshProducts ]);

  const reportNetworkError = (e, msg) => {
    window.alert('Network error: ' + msg);
    console.error(e);

    Networker.sendError({
      message: msg,
      namespace: 'dashboard.connectionstats',
      data: e
    });
  }

  const convertToJSON = (data, headers) => {
    const json = [];

    data.forEach((row) => {
      if (row.errors.length > 0) { return; }
      const consumer = { optional: {} }

      for (let j = 0; j < headers.length; j++) {
        const currHeader = headers[j].trim().toLowerCase();
        consumer[currHeader] = row.data[j];
      }

      if (consumer.email && consumer.group) {
        json.push(consumer);
      }
    });

    return json;
  }

  // This can get called before the above on a page reload since we can't await in the above useEffect.
  // This is a bug and should probably be fixed at some point.
  const loadConnectionData = useCallback(async function (productId, isConsumer) {
    let ws;

    try {
      const ws = await reconnectSocket();

      const path = isSuperUser ? '/product/usersWithDeviceStatus' : '/product/users';

      const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      
      // Track active requestId so stale frames don't affect UI
      activeRequestIdRef.current = requestId;

      // Remove any prior listener
      if (progressListenerRef.current) {
        try { ws.removeEventListener('message', progressListenerRef.current); } catch (_) {}
        progressListenerRef.current = null;
      }

      const onMessage = (evt) => {
        let msg;
        try {
          msg = typeof evt.data === 'string' ? JSON.parse(evt.data) : evt.data;
        } catch {
          return;
        }

        // Only accept progress frames for THIS requestId
        if (!msg || msg.type !== 'progress') return;
        if (msg.requestId !== activeRequestIdRef.current) return;

        // Drive the bar upward on each keepalive/progress tick
        bumpLoadBarTowardCap();
      };

      ws.addEventListener('message', onMessage);
      progressListenerRef.current = onMessage;

      const { info, ok } = await InternalApi.request(ws, path, {
        consumers: isConsumer,
        id: productId,
        requestId
      });

      // Detach listener
      if (progressListenerRef.current) {
        try { ws.removeEventListener('message', progressListenerRef.current); } catch (_) {}
        progressListenerRef.current = null;
      }

      if (!ok) {
          throw new Error('Request failed!');
      }

      const headers = [
        { email: "Email", width: 250 },
        { labels: "Macs", width: 150 },
        { lastInteractionDate: "Last Interaction Detected", width: 250 },
        { lastConnectionMs: "Last Connection (Days)", width: 250 },
        { registeredAt: "Registered Date", width: 200 },
        { connectedAt: "Connected Date", width: 200 },
        { firstUseAt: "First Use Detected Date", width: 250 },
        { lastUseAt: "Last Use Detected Date", width: 250 },
        { interactions: "Interaction Count", width: 200 },
        { uses: "Uses Count", width: 175 },
        // { switchedCount: "Band Switches", width: 175 },
        { phone: "Phone Number", width: 175 },
        { mobileSystem: "Phone OS", width: 150 },
        { battery: "Battery Life", width: 150 },
        { participantId: "Participant ID", width: 175 },
        { userId: "User ID", width: 225 },
      ];

      if (isSuperUser) {
        headers.push(
          { heartbeatCount: 'Heartbeats', width: 200 },
          { forcedCount: 'Forced', width: 200 },
          { resetCount: 'Resets', width: 200 },
          { timedCount: 'Timed', width: 200 },
          { lastMeasurementType: 'Last Measurement Type', width: 200 },
          { latestResetAt: 'Latest Reset', width: 200 },
          { lastPushSentAt: 'Push Last Sent', width: 200 },
          { lastPushReadAt: 'Push Last Read', width: 200 },
          { lastEmailSentAt: 'Email Last Sent', width: 200 },
          { lastEmailReadAt: 'Email Last Read', width: 200 },
          { lastSmsSentAt: 'SMS Last Sent', width: 200 },
          { lastSmsReadAt: 'SMS Last Read', width: 200 },
          { lastRepeatNmSentAt: 'Last repeatEveryNm Sent', width: 220 },
          { deviceVendorIdentifier: 'Device Vendor Identifier', width: 260 },
          { deviceAppState: 'App State', width: 160 },
          { deviceBluetoothState: 'Bluetooth State', width: 180 },
          { deviceScanRunning: 'Scan Running', width: 160 },
          { deviceStatusUpdatedAt: 'Device Status Updated', width: 240 },
          { devicePermissionBluetooth: 'Bluetooth Permission', width: 200 },
          { devicePermissionLocation: 'Location Permission', width: 200 },
          { devicePermissionPush: 'Push Permission', width: 180 },
          { devicePermissionsUpdatedAt: 'Permissions Updated', width: 240 },
          { notificationPushEnabled: 'Push Enabled', width: 150 },
          { notificationSmsEnabled: 'SMS Enabled', width: 150 },
          { notificationEmailEnabled: 'Email Enabled', width: 150 },
          { firebaseToken: 'Firebase Token', width: 250 },
        );
      }

      // Add any extra headers (notifications, sent surveys, etc.) that are per-product.
      if (info?.length)
      {
        // Insert fields for volumetric products
        if (Object.hasOwn(info[0], 'remaining'))
        {
          headers.splice(9, 0,
            { remaining: 'Amount Remaining (G)', width: 225 },
            { isEmpty: 'Empty', width: 150 },
            { needsRefillCount: 'Needs Refill Count', width: 200 },
            { refilledCount: 'Refill Count', width: 200 },
          );
        }

        // Add this field if we have no connection notification for this product
        if (Object.hasOwn(info[0], 'lastNotifiedNoConnection')) {
          headers.splice(3, 0, { lastNotifiedNoConnection: 'Last No Connection Notified', width: 300 });
        }

        const cols = Object.keys(info[0]).filter((k) => ![ '_id' ].includes(k));
        const pre = headers.map((o) => Object.keys(o)[0]);

        headers.push.apply(headers, cols.filter((k) => !pre.includes(k)).map((k) => ({ [k]: k, width: 200 })));
      }

      const data = info.map((consumer, index) => {
        const entry = Object.assign({ id: index }, consumer);
        delete entry._id;

        if (consumer.labels) {
            entry.labels = consumer.labels.join(', ')
        }

        if (consumer.lastConnectionMs) {
            entry.lastConnectionMs = (consumer.lastConnectionMs / (24 * 60 * 60 * 1000)).toFixed(3);
        }

        if (consumer.remaining) {
            entry.remaining = consumer.remaining.toFixed(2);
        }

        if (consumer.battery) {
            entry.battery = consumer.battery.toFixed(4);
        }

        console.info('+', entry);
        return entry;
      });

      return {
          headers: headers,
          data: data
      };
    } catch (err) {
      console.error('Failed to fetch data:', err);
      // Detach listener on error
      if (ws && progressListenerRef.current) {
        try { ws.removeEventListener('message', progressListenerRef.current); } catch (_) {}
        progressListenerRef.current = null;
      }

      cancelLoadBar();
      alert('Internal error while fetching data!')

      return { headers: [], data: [] };
    }
  // eslint-disable-next-line
  }, [isSuperUser]);

  const reloadData = useCallback(async function () {
      if (!currentTab || !hasAccess) { return; }

      console.info('Loading data.');
      setSelectionModel([]);
      setTableRows([]);
      setLoading(true);
      startLoadBar('Loading Product Data...');

      try {
        await reconnectSocket();
      } catch {
        setLoading(false);
        cancelLoadBar();
        return;
      }

      // First look for data with consumers.
      let res = await loadConnectionData(currentTab, true);

      // Otherwise look without consumers.
      if (!res.data?.length) {
          res = await loadConnectionData(currentTab, false);
          setConsumerData(false);
      } else {
          // We have consumer data
          setConsumerData(true);
      }

      // This allows the table to work properly
      res.data.forEach((obj, index) => obj.id = index);

      setTableHeaders(res.headers);
      setTableRows(res.data);

      setLoading(false);
      // setLoadProgress(null);
      await finishLoadBar();
      // eslint-disable-next-line
  }, [ loadConnectionData, currentTab, hasAccess ]);

  const enrichedRows = React.useMemo(() => {
    return tableRows.map((row, index) =>
      enrichConnectionRow({ ...row, id: index }, thresholds)
    );
  }, [tableRows, thresholds]);

  const filteredRows = React.useMemo(() => {
    if (!activeCriteria.length) {
      return enrichedRows;
    }

    return enrichedRows.filter((row) =>
      activeCriteria.every((key) => CRITERIA_DEFINITIONS[key]?.matches(row))
    );
  }, [enrichedRows, activeCriteria]);

  const computedStats = React.useMemo(() => {
    const stats = {};

    Object.entries(CRITERIA_DEFINITIONS).forEach(([key, def]) => {
      stats[key] = enrichedRows.filter(def.matches).length;
    });

    return { stats };
  }, [enrichedRows]);

  const selectedRows = React.useMemo(() => {
    const selectedIds = new Set(selectionModel);
    return enrichedRows.filter((row) => selectedIds.has(row.id));
  }, [enrichedRows, selectionModel]);

  useEffect(() => {
    if (!hasAccess) { return; }

    let activeSocket;
    (async () => {
      try {
        activeSocket = await reconnectSocket();
        console.info('connected.');
        await reloadData();
      } catch (err) {
        console.error('Failed to connect to socket:', err);
      }
    })();

    return () => {
      if (activeSocket) {
        InternalApi.disconnect(activeSocket);
      }
    };

    // This function will keep looping if we don't add this
    // eslint-disable-next-line
  }, [ hasAccess ]);

  // Reload page data when the current product is changed.
  // eslint-disable-next-line
  useEffect(reloadData, [ currentTab ])

  const handleSendBulkNotification = async (payload) => {
    console.info('Sending bulk notification with payload:', payload);

    const res = await Networker.post({
      root: 'notifications',
      inner: 'manual/send',
      body: payload,
    });

    return res.body;
  };

  // Uploading CSV File Function
  const handleFileLoad = (data, productId) => {
    const headers = data[0].data.map((header) => header.trim().toLowerCase());

    if (!headers.includes('email') || !headers.includes('group'))
    {
        window.alert('CSV file must contain the email column and the group column!');
        return;
    }

    // Convert CSV to JSON
    const jsonData = convertToJSON(data.slice(1), headers);

    // Save the new data
    setConsumerData(true);

    // Save data and disable loading indicator when done.
    saveRecruitedConsumers(jsonData, productId).then(reloadData);
  };

  const saveRecruitedConsumers = async (consumers, productId) => {
    try {
      setLoading(true);

      await Networker.post({
        root: 'recruitedConsumers',
        inner: '',
        body: consumers,
        query: { productId: productId },
        cache: true
      });

      window.alert('Consumer data updated.');
    } catch (e) {
      reportNetworkError(e, 'Failed to save recruited consumers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecruitedConsumers = (productId) => {
    if (window.confirm('Are you sure you want to delete the recruited consumers for this product?')) {
      saveRecruitedConsumers([], productId).then(reloadData);
    }
  }

  const downloadEmailMismatchTable = (data, productId) => {
    // FIXME: because there is some misalignment in the EmailConsumersStatTable component, 
    // the headers have to be incorrectly mapped here for the headers to be correct 
    const headers = [
      { email: "Account Email" },
      { accountedEmail: "Recruited Email" },
      { phone: "Phone Number" },
      { macs: "Macs" },
      { userId: "User Id" }
    ];

    handleDownload(data, headers, productId, 2);
  }

  const handleDownload = (data, headers, productId, caseVal) => {
    if (caseVal === 1) headers.push({ email: "Email" }, { alert: "Alert" });
    const { field: sortField, order: sortOrder } = sortObject;

    const dataDownload = data.sort((a, b) => a[sortField] > b[sortField] ? sortOrder : -sortOrder).map(row => {
      const updatedRow = {};

      headers.forEach(headerObj => {
        const [headerKey, headerValue] = Object.entries(headerObj)[0];

        if (row[headerKey] === undefined) {
          updatedRow[headerValue] = "";
        } else {
          updatedRow[headerValue] = row[headerKey];
        }
      });

      return updatedRow;
    });

    if (dataDownload.length) {
      const currentProd = products.filter(prod => prod._id === productId)[0];
      downloadObjectData(dataDownload, currentProd.name);
    }
  }

  const handleSortByField = (field, order) => { setSortObject({ field, order }); }

  const getconsumersList = async () => {
    setShowMatchedEmails(false);
    let filterRes;

    const filterResData = await Networker.get({
      root: 'recruitedConsumers/filter',
      inner: '',
      query: { productId: currentTab },
      cache: true
    });

    let updatedPotentialMatchedEmails = [];
    if (filterResData && filterResData.body) {
      filterRes = {};
      filterRes = filterResData;
      ConsumersListDetailsFromApi.current = filterResData.body;
    }
    if (filterRes && filterRes.body && filterRes.body.emailList.length > 0) {
      // setLoading(true);
      filterRes.body.emailList.map((item, index) => {
        // newEmailList.push({ recruiteEmail: item.email })
        filterRes.body.emailList[index]['phoneNumberChecked'] = false;
        filterRes.body.emailList[index]['recruitedEmailChecked'] = false;
        filterRes.body.emailList[index]['matchedEmailChecked'] = false;
        if (!filterRes.body.emailList[index].hasOwnProperty('extraAccountedEmails'))
          filterRes.body.emailList[index]['AllEmailDataAreThere'] = true;
        if (item.matchedEmail && item.matchedEmail.indexOf(',') > 0) {
          filterRes.body.emailList[index]['potentialEmails'] = item.matchedEmail.split(',');
          if (filterRes.body.emailList[index]['potentialEmails'] && filterRes.body.emailList[index]['potentialEmails'].length > 0) {
            filterRes.body.emailList[index]['potentialEmails'].map((matchedEmail) => {
              updatedPotentialMatchedEmails.push({
                potentialEmailChecked: false,
                potentialEmail: matchedEmail
              });
              return true;
            });
            filterRes.body.emailList[index]['potentialEmails'] = updatedPotentialMatchedEmails;
            updatedPotentialMatchedEmails = [];
          } else {
            filterRes.body.emailList[index]['potentialEmails'] = [];
          }
        } else {
          filterRes.body.emailList[index]['potentialEmails'] = [];
        }
        return true
      });
      if (filterRes.body.emailList.length > 0 && filterRes.body.presentInvalidData.length === 0) {
        setLoading(false);
        setConsumersStackData(filterRes.body.emailList.length > 0 ? filterRes.body.emailList : []);
        setOpenConsumersStack(true);
      }
    } if (filterRes && filterRes.body && filterRes.body.presentInvalidData.length > 0) {
      let finalPresentInvalidData = [];
      // if (filterRes.body.presentInvalidData.length > 0 && filterRes.body.emailList.length === 0) finalPresentInvalidData = filterRes.body.presentInvalidData;
      filterRes.body.presentInvalidData.map((item) => {
        // newEmailList.push({ recruiteEmail: item.email });
        if (filterRes.body.emailList.length > 0) {
          let matchedIndex = filterRes.body.emailList.findIndex((emails) => emails.matchedEmail === item.email);
          const againMatchIndex = filterRes.body.emailList.findIndex((emails) => emails.accountedEmail === item.email);
          if (matchedIndex > -1) {
            filterRes.body.emailList[matchedIndex]['accountedEmail'] = item.email;
            filterRes.body.emailList[matchedIndex]['accountedEmailChecked'] = false;
          } else if (matchedIndex === -1) {
            let potentioalEmailIndex = -1;
            filterRes.body.emailList.map((emailItem, index) => {
              if (emailItem.potentialEmails && emailItem.potentialEmails.length > 0) {
                potentioalEmailIndex = emailItem.potentialEmails.findIndex((potential) => potential.potentialEmail === item.email);
                matchedIndex = index;
                const againMatchIndexToAdd = filterRes.body.emailList.findIndex((emails) => emails.accountedEmail === item.email);
                if (potentioalEmailIndex > -1 && againMatchIndexToAdd === -1 &&
                  !filterRes.body.emailList[index].accountedEmailMatchedEmail) {
                  filterRes.body.emailList[index]['accountedEmail'] = item.email;
                  filterRes.body.emailList[index]['accountedEmailChecked'] = false;
                  filterRes.body.emailList[index]['accountedEmailMatchedEmail'] = true;
                }
              }
              return true;
            });
            if (potentioalEmailIndex === -1 && matchedIndex === -1 && againMatchIndex === -1) {
              filterRes.body.emailList.push({
                'accountedEmail': item.email,
                'accountedEmailChecked': false,
                'extraAccountedEmails': true
              });
            }
            const againMatch = filterRes.body.emailList.findIndex((emails) => emails.accountedEmail === item.email);

            if (againMatch === -1 && againMatchIndex === -1) {
              filterRes.body.emailList.push({
                'accountedEmail': item.email,
                'accountedEmailChecked': false,
                'extraAccountedEmails': true
              });
            }
          }
        } else {
          finalPresentInvalidData.push({
            'accountedEmail': item.email,
            'accountedEmailChecked': false
          });
        }
        return true
      });
      setLoading(false);
      setConsumersStackData(filterRes.body.emailList.length > 0 ? filterRes.body.emailList : finalPresentInvalidData);
      setOpenConsumersStack(true);
    }
    else {
      setOpenConsumersStack(true);
      setLoading(false);
      setConsumersStackData([]);
    }
  };

  const getConsumersStatsList = () => {
    getconsumersList();
    setConsumersStackData([]);
  }

  useEffect(() => {
    if (!currentTab && products.length > 0) {
      history.push('/connectionstatistics/' + products[0]._id)
    } else if (products.length === 0) {
      refreshProducts();
    }
  }, [currentTab, history, products, refreshProducts]);

  // hide and show the right side bar
  const toggleSidebar = () => setSidebarVisible(!sidebarVisible);

  const handleTabChange = (val) => (_) => {
    if (val !== currentTab) {
      history.push('/connectionstatistics/' + val);
      setSortObject({ field: 'email', order: 1 });
    }
  };

  const closeConsumerStats = () => {
    setOpenConsumersStack(false);
    setConsumersStackData([]);
  }

  const classes = withStyle();

  // I made this too fancy for no reason. Technically you could just compute the right hand side's height directly, but I'm too lazy.
  const maxListHeight = (() => {
      const tableHeight = (tableRows.length > rowCount ? rowCount : tableRows.length) * 37.5;
      const windowHeight = window.innerHeight - 250;

      return (windowHeight > tableHeight) ? windowHeight : tableHeight;
  })();

  return (<div className={classes.root}><CssBaseline /><Loader show={loading} />
    <AppContainer classes={classes} title="Connection Statistics" match={match}>
      <Grid container direction="row" style={{ marginTop: 64, marginBottom: 16 }} alignItems="stretch" justifyContent="flex-start" spacing={2}>
        {/* Left Section */}
        {sidebarVisible && (<Grid item xs={12} md={4}>
          <Paper style={{ paddingLeft: 8, paddingRight: 8 }}  elevation={3}>
            {/* List Header/Search Bar */}
            <Grid container direction="row" spacing={1} style={{ marginTop: 0, paddingTop: 8 }} className={classes.drawerHeader}>
              <Grid item xs={2}><IconButton edge="left" color="inherit" onClick={toggleSidebar}><MenuIcon /></IconButton></Grid>
              <Grid item xs={10}>
                <TextField fullWidth type="text" placeholder="Search..." value={query} onChange={handleChangeQuery} variant="outlined" />
              </Grid>
            </Grid>
            {/* Displayed list */}
            <List style={{ overflowY: 'auto', maxHeight: maxListHeight, height: '100%' }} dense>
              {displayProducts.map(prod => (
                <ListItem key={prod._id} button divider dense selected={currentTab === prod._id} onClick={handleTabChange(prod._id)}>
                  <ListItemAvatar><Avatar alt={prod.name.substr(0, 3)} src={prod.iconURL} /></ListItemAvatar>
                  <ListItemText 
                    primary={prod.name} 
                    secondary={
                      <Typography variant="caption" color="textSecondary">
                        {prod.productId}
                      </Typography>
                    } />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>)}
        {/* Right Section */}
        <Grid item xs={12} md={sidebarVisible ? 8 : 12}>
          <Paper style={{ paddingLeft: 8, paddingRight: 8, paddingBottom: 8 }} elevation={3}>
            {/* Right Sidebar Content */}
            <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2} style={{ marginTop: 0, paddingBottom: 8 }}>
              {!sidebarVisible ? (
                  <Grid item xs={0.5}><IconButton edge="left" color="inherit" onClick={toggleSidebar}><MenuIcon /></IconButton></Grid>
              ) : null}
              {/* Additional Buttons */}
              {isSuperUser && (
                  <Grid item xs={4}>
                    <CSVUploader
                      onLoad={(data) => {
                        handleFileLoad(data, currentTab);
                      }}
                    />
                  </Grid>
                )}
              <Grid item xs={3}>
                <Button fullWidth disabled={!consumerData} type="submit" variant="contained" color="primary"
                  onClick={() => { setLoading(true); getConsumersStatsList() }}>
                  Consumer Info
                </Button>
              </Grid>
              {isSuperUser && (
                  <Grid item xs={4}>
                    <Button fullWidth disabled={!consumerData} type="submit" variant="contained" color="primary" onClick={() => handleDeleteRecruitedConsumers(currentTab)}>
                      Delete Consumers
                    </Button>
                  </Grid>
                )}
            </Grid>
            {/* Connection Data View */}
            {(!!tableRows?.length ? <DataGrid
              rows={filteredRows}
              columns={tableHeaders.map((header) => {
                const field = Object.keys(header)[0];
                const headerName = header[field];

                return {
                  headerName,
                  field,
                  width: header.width,
                  renderHeader: () => (
                    <div
                      style={{
                        width: '100%',
                        overflowX: 'auto',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {headerName}
                    </div>
                  )
                };
              })}
              rowsPerPageOptions={[ 20, 30, 50, 100]}
              pageSize={rowCount}
              onPageSizeChange={(val) => setRowCount(val)}
              disableSelectionOnClick 
              checkboxSelection
              autoHeight
              density="compact"
              onSortByField={handleSortByField}
              sortObject={sortObject}
              onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
              components={{ Toolbar: ConnectionStatsToolbar }}
              componentsProps={{
                toolbar: {
                  isSuperUser,
                  stats: computedStats.stats,
                  activeCriteria,
                  setActiveCriteria,
                  thresholds,
                  setThresholds,
                  selectedCount: selectionModel.length,
                  onOpenBulkNotify: () => setBulkNotifyOpen(true),
                },
              }}
            /> : 
            (
              loading ? (
                <div style={{ margin: 32 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="textSecondary">
                      {loadBar.label || 'Loading Product Data...'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {Math.round(loadBar.value)}%
                    </Typography>
                  </div>
                  <LinearProgress variant="determinate" value={loadBar.value} />
                </div>
              ) : (
                <Typography variant="h5" align="center" style={{ margin: 32 }}>
                  No Data Available
                </Typography>
              )
            ))}
          </Paper>
          {/* left section */}
          {/* You can place your DataGrid component here (Depend on Review) */}
        </Grid>
      </Grid>
      {/* consumers stack modal along with table */}
      {
        openConsumersStack && <ConsumersTable
          show={openConsumersStack}
          consumersStackList={consumersStackData.length > 0 ? consumersStackData : []}
          currentTabId={currentTab}
          downloadEmailMismatchTableApi={downloadEmailMismatchTable}
          ShowMatchedEmailsList={ShowMatchedEmails}
          closePopup={closeConsumerStats}
          ConsumersListDetailsDataFromApi={ConsumersListDetailsFromApi.current}
          exportCosntStatsToCSVApi={downloadEmailMismatchTable}
        />
      }
      {/*end of consumers table  */}
      {/* match filter */}
      <Dialog
        open={bulkNotifyOpen}
        fullWidth
        maxWidth="md"
        onClose={() => setBulkNotifyOpen(false)}
      >
        <DialogTitle>Send Message to Selected Users</DialogTitle>
        <DialogContent>
          <BulkNotificationEditor
            selectedRows={selectedRows}
            currentProductId={currentTab}
            productData={currentProduct}
            onSendNotification={handleSendBulkNotification}
            onClose={() => setBulkNotifyOpen(false)}
          />
        </DialogContent>
      </Dialog>
    <MadeWithLove />
    </AppContainer >
  </div>);
}

const toolbarStyles = {
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    rowGap: 8,
    columnGap: 8,
    padding: '8px 8px 4px 8px',
  },
  group: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  thresholdsGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: 12,
    marginLeft: 12,
    width: '100%',
  },
  criteriaGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    marginTop: 4,
  },
  fieldBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 120,
  },
  fieldLabel: {
    lineHeight: 1.2,
    marginBottom: 4,
    whiteSpace: 'nowrap',
  },
  textField: {
    width: 150,
  },
  button: {
    minWidth: 120,
  },
  spacer: {
    flex: 1,
    minWidth: 16,
  },
  sendButton: {
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
  },
};
